'use client'

import React, { useCallback, useEffect, useId, useRef, useState } from 'react'

import Icon from '@/components/atoms/Icon'

import { AccordionProps, AccordionListProps } from './Accordion.types'
import styles from './Accordion.module.scss'

export const Accordion = ({
  head,
  children,
  theme = 'light',
  expanded: externalExpanded,
  disabled = false,
  onChange,
  className = '',
  backgroundColor = undefined,
  isAccordionList = false,
}: AccordionProps) => {
  const renderContent = (content: React.ReactNode | string) => (
    <div
      className={styles.accordion__content_inner}
      {...(typeof content === 'string'
        ? { dangerouslySetInnerHTML: { __html: content } }
        : { children: content })}
    />
  )

  const [expanded, setExpanded] = useState(externalExpanded ?? false)
  const contentRef = useRef<HTMLDivElement>(null)
  const headerId = useId()
  const contentId = useId()

  useEffect(() => {
    if (externalExpanded !== undefined) {
      setExpanded(externalExpanded)
    }
  }, [externalExpanded])

  useEffect(() => {
    if (!contentRef.current) return

    const element = contentRef.current

    if (expanded) {
      const contentHeight = element.scrollHeight
      requestAnimationFrame(() => {
        element.style.height = `${contentHeight}px`
        const timer = setTimeout(() => {
          element.style.height = 'auto'
        }, 300)
        return () => clearTimeout(timer)
      })
    } else {
      if (element.style.height === 'auto') {
        element.style.height = `${element.scrollHeight}px`
        // Force reflow
        void element.offsetHeight
      }
      requestAnimationFrame(() => {
        element.style.height = '0'
      })
    }
  }, [expanded])

  const handleToggle = useCallback(() => {
    if (disabled) return
    const newExpanded = !expanded
    setExpanded(newExpanded)
    onChange?.(newExpanded)
  }, [expanded, disabled, onChange])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (disabled) return
      switch (event.key) {
        case 'Enter':
        case ' ':
          event.preventDefault()
          handleToggle()
          break
        case 'ArrowUp':
        case 'ArrowDown':
          // Prevent page scroll
          event.preventDefault()
          break
      }
    },
    [disabled, handleToggle]
  )

  const accordionClasses = [
    styles.accordion,
    styles[`accordion--${theme}`],
    expanded ? styles['accordion--expanded'] : '',
    disabled ? styles['accordion--disabled'] : '',
    !isAccordionList && backgroundColor === 'color-scheme'
      ? styles['accordion--not-list']
      : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const inlineStyles = {
    backgroundColor:
      backgroundColor !== 'transparent' ? backgroundColor : undefined,
  }

  return (
    <div
      className={accordionClasses}
      data-color-scheme={theme}
      data-state={expanded ? 'open' : 'closed'}
      data-background={backgroundColor}
      style={inlineStyles}
    >
      <h3 className={styles.accordion__header_wrapper}>
        <button
          id={headerId}
          className={styles.accordion__header}
          onClick={handleToggle}
          onKeyDown={handleKeyDown}
          aria-expanded={expanded}
          aria-controls={contentId}
          aria-disabled={disabled}
          disabled={disabled}
        >
          <span className={styles.accordion__header_content}>{head}</span>
          <Icon
            iconName={'ChevronDown'}
            className={`${styles.accordion__icon} ${
              expanded ? styles['accordion__icon--open'] : ''
            }`}
            aria-hidden={true}
          />
        </button>
      </h3>
      <div
        id={contentId}
        role='region'
        aria-labelledby={headerId}
        className={styles.accordion__content}
        ref={contentRef}
      >
        {renderContent(children)}
      </div>
    </div>
  )
}

export const AccordionList = ({
  items,
  theme = 'light',
  allowMultiple = false,
  backgroundColor = undefined,
  className = '',
}: AccordionListProps) => {
  const [expandedIndexes, setExpandedIndexes] = useState<number[]>([])
  const containerRef = useRef<HTMLDivElement>(null)

  const handleAccordionChange = (index: number, expanded: boolean) => {
    setExpandedIndexes((prev) => {
      if (expanded) {
        if (allowMultiple) {
          return [...prev, index]
        }
        return [index]
      }
      return prev.filter((i) => i !== index)
    })
  }

  const listClasses = [
    styles.accordion_list,
    styles[`accordion_list--${theme}`],
    backgroundColor === 'color-scheme'
      ? styles['accordion_list--color-scheme']
      : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div
      ref={containerRef}
      className={listClasses}
      role='presentation'
    >
      {items.map((item, index) => (
        <Accordion
          key={index}
          head={item.head}
          theme={theme}
          expanded={expandedIndexes.includes(index)}
          disabled={item.disabled}
          onChange={(expanded) => handleAccordionChange(index, expanded)}
          isAccordionList={true}
        >
          {item.content}
        </Accordion>
      ))}
    </div>
  )
}
